/**
 * Brother PES version 1 ("#PES0001").
 *
 * Layout: signature, u32 offset of the PEC section, PES header (scale-to-fit, hoop, number of
 * block objects), a "CEmbOne" object with placement transform, a "CSewSeg" object listing
 * stitch segments (stitch runs and jumps with their PEC colour index) and a colour log,
 * followed by the complete PEC section which is what Brother/Baby Lock machines sew.
 *
 * Colours: PES v1 stores palette indices of the 64-colour PEC table, not RGB. Colours are
 * mapped to the nearest PEC thread (distinct design colours get distinct entries when possible).
 *
 * Reference: EmbroidePy/pyembroidery (MIT) PesWriter; independent implementation.
 */
import { t } from '../i18n/i18n';
import { CMD } from '../engine/stitch/sequence';
import { ByteReader, ByteWriter, FormatError } from './bytes';
import { type FormatLimits, type MachinePattern, type MachineStitch, extents } from './machine';
import { type PecSection, readPecSection, writePecSection } from './pec';
import { PEC_THREADS, hexToRgb, nearestIndex, rgbToHex } from './threads';

export const PES_LIMITS: FormatLimits = { maxStitch: 2047, maxJump: 2047, maxCoord: 32767 };

function pesString16(w: ByteWriter, s: string): void {
  w.u16le(s.length);
  w.ascii(s);
}

interface Segment {
  points: { x: number; y: number }[];
  color: number;
  flag: number;
}

/** Groups commands into PES segments: stitch runs (flag 0) and jumps (flag 1). */
function segments(p: MachinePattern, ax: number, ay: number, colorOf: (threadIndex: number) => number): Segment[] {
  const out: Segment[] = [];
  let threadIndex = 0;
  let color = colorOf(threadIndex++);
  let sx = 0, sy = 0;
  const st = p.stitches;
  let i = 0;
  while (i < st.length) {
    const cmd = st[i].cmd;
    let j = i;
    while (j < st.length && st[j].cmd === cmd) j++;
    const block = st.slice(i, j);
    if (cmd === CMD.JUMP) {
      const last = block[block.length - 1];
      out.push({ points: [{ x: sx - ax, y: sy - ay }, { x: last.x - ax, y: last.y - ay }], color, flag: 1 });
    } else if (cmd === CMD.COLOR_CHANGE) {
      color = colorOf(threadIndex++);
    } else if (cmd === CMD.STITCH) {
      const pts = block.map((s) => ({ x: s.x - ax, y: s.y - ay }));
      sx = block[block.length - 1].x;
      sy = block[block.length - 1].y;
      out.push({ points: pts, color, flag: 0 });
    }
    i = j;
  }
  return out;
}

/** Writes the CEmbOne/CSewSeg block objects and returns the colour log [section, colour]. */
function writeBlocks(w: ByteWriter, p: MachinePattern, colorOf: (threadIndex: number) => number): [number, number][] {
  const e = extents(p.stitches);
  const width = e.maxX - e.minX;
  const height = e.maxY - e.minY;
  pesString16(w, 'CEmbOne');
  for (let k = 0; k < 8; k++) w.u16le(0);
  // placement transform (identity + translation inside a 130 × 180 mm page)
  const transX = 350 + 1300 / 2 - width / 2;
  const transY = 100 + height + 1800 / 2 - height / 2;
  w.f32le(1).f32le(0).f32le(0).f32le(1).f32le(transX).f32le(transY);
  w.u16le(1).u16le(0).u16le(0).u16le(width).u16le(height);
  w.fill(0, 8);
  const sectionsAt = w.pos;
  w.u16le(0);
  w.u16le(0xffff).u16le(0);
  pesString16(w, 'CSewSeg');
  const segs = segments(p, e.minX, e.maxY, colorOf);
  const colorLog: [number, number][] = [];
  let prevColor = -1;
  segs.forEach((s, idx) => {
    if (idx > 0) w.u16le(0x8003);
    if (s.color !== prevColor) {
      colorLog.push([idx, s.color]);
      prevColor = s.color;
    }
    w.u16le(s.flag).u16le(s.color).u16le(s.points.length);
    for (const pt of s.points) w.i16le(pt.x).i16le(pt.y);
  });
  w.u16le(colorLog.length);
  for (const [sec, c] of colorLog) w.u16le(sec).u16le(c);
  w.patch(sectionsAt, (x) => x.u16le(segs.length));
  w.u16le(0).u16le(0);
  return colorLog;
}

const hasMoves = (p: MachinePattern): boolean => p.stitches.some((s) => s.cmd === CMD.STITCH || s.cmd === CMD.JUMP);

function checkStops(p: MachinePattern): void {
  if (p.stitches.some((s) => s.cmd === CMD.STOP)) throw new FormatError(t('PES does not support stop commands without a colour change.'));
}

/** PES version 1: colours as PEC palette indices. Most widely compatible. */
export function writePes(p: MachinePattern): Uint8Array {
  checkStops(p);
  const w = new ByteWriter();
  w.ascii('#PES0001');
  const pecOffsetAt = w.pos;
  w.u32le(0);
  if (!hasMoves(p)) {
    w.u16le(1).u16le(1).u16le(0);
    w.u16le(0).u16le(0);
  } else {
    w.u16le(1).u16le(1).u16le(1); // scale to fit, hoop 130x180, one block object
    w.u16le(0xffff).u16le(0);
    writeBlocks(w, p, (i) => nearestIndex(hexToRgb(p.threads[i]?.hex ?? '#000000'), PEC_THREADS));
  }
  const pecOffset = w.pos;
  w.patch(pecOffsetAt, (x) => x.u32le(pecOffset));
  writePecSection(p, w);
  return w.toBytes();
}

const enc = new TextEncoder();
function pesString8(w: ByteWriter, s: string | undefined | null): void {
  if (!s) {
    w.u8(0);
    return;
  }
  const b = enc.encode(s).subarray(0, 255);
  w.u8(b.length);
  w.bytes(b);
}

/**
 * PES version 6 ("#PES0060"): the header carries the exact RGB thread list (with names), block
 * segments reference that list, and an addendum after the PEC section repeats the RGB values.
 * The PEC section (what machines sew) is identical to version 1.
 */
export function writePes6(p: MachinePattern): Uint8Array {
  checkStops(p);
  const w = new ByteWriter();
  w.ascii('#PES0060');
  const pecOffsetAt = w.pos;
  w.u32le(0);
  const moves = hasMoves(p);
  // header
  w.u16le(1); // hoop 100x100 / 130x180 flag
  w.ascii('02');
  pesString8(w, p.name);
  pesString8(w, null); // category
  pesString8(w, null); // author
  pesString8(w, null); // keywords
  pesString8(w, null); // comments
  for (const v of [0, 0, 0x64, 0x64, 0, 0xc8, 0xc8, 0x64, 0x64, 0x64, 0x07, 0x13, 1, 1, 0, 100, 1, 0]) w.u16le(v);
  w.u8(0); // no source image file name
  w.f32le(1).f32le(0).f32le(0).f32le(1).f32le(0).f32le(0);
  w.u16le(0).u16le(0).u16le(0); // programmable fills, motifs, feather patterns
  w.u16le(p.threads.length);
  for (const t of p.threads) {
    const rgb = hexToRgb(t.hex);
    pesString8(w, t.code);
    w.u8((rgb >> 16) & 0xff).u8((rgb >> 8) & 0xff).u8(rgb & 0xff).u8(0);
    w.u32le(0x0a); // custom colour
    pesString8(w, t.name);
    pesString8(w, t.brand);
    pesString8(w, null); // chart
  }
  const chart = p.threads.map((t) => ({ rgb: hexToRgb(t.hex), name: '', code: '' }));
  if (!moves) {
    w.u16le(0);
    w.u16le(0).u16le(0);
  } else {
    w.u16le(1); // one block object
    w.u16le(0xffff).u16le(0);
    const log = writeBlocks(w, p, (i) => nearestIndex(hexToRgb(p.threads[i]?.hex ?? '#000000'), chart));
    w.u32le(0).u32le(0);
    for (let i = 0; i < log.length; i++) w.u32le(i).u32le(0);
  }
  const pecOffset = w.pos;
  w.patch(pecOffsetAt, (x) => x.u32le(pecOffset));
  const indices = writePecSection(p, w);
  // addendum: PEC palette indices (with count prefix), padding, per-thread blocks, RGB list
  const list = indices.length ? [indices.length - 1, ...indices] : [];
  w.bytes(list);
  w.fill(0x20, 128 - list.length);
  for (let i = 0; i < p.threads.length; i++) w.fill(0, 0x90);
  for (const t of p.threads) w.u24le(hexToRgb(t.hex));
  w.u16le(0);
  return w.toBytes();
}

export interface PesThread {
  hex: string;
  code: string;
  name: string;
  brand: string;
}

export interface PesFile {
  version: string;
  pecOffset: number;
  pec: PecSection;
  /** Segments from the CSewSeg block, when present. */
  segments: { flag: number; color: number; points: { x: number; y: number }[] }[];
  /** Exact thread colours from the PES v6 header (empty for v1). */
  threads: PesThread[];
}

const dec = new TextDecoder();
function readString8(r: ByteReader): string {
  const n = r.u8();
  const start = r.pos;
  r.skip(n);
  return dec.decode(r.data.subarray(start, start + n));
}

function readBlocks(r: ByteReader, segs: PesFile['segments']): void {
  r.skip(4);
  const n1 = r.u16le();
  if (r.ascii(n1) !== 'CEmbOne') throw new FormatError(t('Expected a CEmbOne block.'));
  r.skip(16 + 24 + 10 + 8);
  const sections = r.u16le();
  r.skip(4);
  const n2 = r.u16le();
  if (r.ascii(n2) !== 'CSewSeg') throw new FormatError(t('Expected a CSewSeg block.'));
  for (let s = 0; s < sections; s++) {
    if (s > 0 && r.u16le() !== 0x8003) throw new FormatError(t('Invalid section separator in CSewSeg.'));
    const flag = r.u16le();
    const color = r.u16le();
    const count = r.u16le();
    const points = [];
    for (let k = 0; k < count; k++) points.push({ x: r.i16le(), y: r.i16le() });
    segs.push({ flag, color, points });
  }
}

export function readPes(bytes: Uint8Array): PesFile {
  const r = new ByteReader(bytes);
  const sig = r.ascii(8);
  if (!sig.startsWith('#PES')) throw new FormatError(t('Not a PES file (missing the #PES signature).'));
  const pecOffset = r.u32le();
  if (pecOffset < 12 || pecOffset >= bytes.length) throw new FormatError(t('Invalid PEC offset in the PES file.'));
  const segs: PesFile['segments'] = [];
  const threads: PesThread[] = [];
  if (sig === '#PES0001') {
    r.u16le();
    r.u16le();
    if (r.u16le() > 0) readBlocks(r, segs);
  } else if (sig === '#PES0060') {
    r.u16le();
    if (r.ascii(2) !== '02') throw new FormatError(t('Unexpected PES v6 header.'));
    for (let i = 0; i < 5; i++) readString8(r); // name, category, author, keywords, comments
    r.skip(18 * 2);
    readString8(r); // source image file name
    r.skip(24 + 6);
    const count = r.u16le();
    for (let i = 0; i < count; i++) {
      const code = readString8(r);
      const rgb = (r.u8() << 16) | (r.u8() << 8) | r.u8();
      r.skip(1 + 4);
      const name = readString8(r);
      const brand = readString8(r);
      readString8(r); // chart
      threads.push({ hex: rgbToHex(rgb), code, name, brand });
    }
    if (r.u16le() > 0) readBlocks(r, segs);
  }
  return { version: sig.slice(4), pecOffset, pec: readPecSection(bytes, pecOffset), segments: segs, threads };
}

export function pesStitches(f: PesFile): MachineStitch[] {
  return f.pec.stitches;
}
